import clientApi from "./client";
import { appendDateRange } from "@/lib/date-utils";

export interface CallScoringFocusMetric {
  dimension_key: string;
  dimension_name: string;
  avg_score: number;
  description: string;
}

export interface CallScoringStatsResponse {
  avg_prep_score: number;
  prep_call_count: number;
  avg_delivery_score: number;
  delivery_call_count: number;
  top_strength: CallScoringFocusMetric | null;
  focus_area: CallScoringFocusMetric | null;
}

export interface GetCallScoringStatsParams {
  email?: string | null;
  startDate?: string;
  endDate?: string;
}

export interface CallScoringAccountApiItem {
  account_id: number;
  account_name: string;
  contact_name: string;
  image?: string | null;
  last_call_date: string | null;
  calls: number;
  avg_prep: number;
  avg_delivery: number;
  outcome: string | null;
  momentum_label: string | null;
  momentum_direction: string | null;
  meeting_id?: number | null;
}

export interface CallScoringAccountsResponse {
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  data: CallScoringAccountApiItem[];
}

export interface GetCallScoringAccountsParams {
  email?: string | null;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface CallScoringDealMomentumBlocker {
  key: string;
  label: string;
  reason: string;
}

export interface CallScoringAccountHeaderResponse {
  account_id: number;
  account_name: string;
  image?: string | null;
  contact_name: string;
  call_count: number;
  avg_prep_score: number;
  avg_delivery_score: number;
  overall_outcome: string | null;
  momentum_label: string | null;
  momentum_direction: string | null;
  has_deal_momentum_data: boolean;
  meeting_id: number | null;
  blockers: CallScoringDealMomentumBlocker[];
}

export interface GetCallScoringAccountHeaderParams {
  accountId: number;
  email?: string | null;
  startDate?: string;
  endDate?: string;
}

export interface CallScoringAccountCallItem {
  meeting_id: number;
  datetime: string;
  prep_score: number | null;
  delivery_score: number | null;
  outcome: string | null;
  duration_minutes?: number | null;
  duration_min?: number | null;
  duration?: number | string | null;
}

export interface CallScoringAccountCallsResponse {
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
  calls: CallScoringAccountCallItem[];
}

export interface GetCallScoringAccountCallsParams {
  accountId: number;
  email?: string | null;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

/** When `entry` is an object, UI shows challenge and response on separate lines. */
export interface CallScoringCallDetailPrepEntryChallengeResponse {
  challenge?: string | null;
  response?: string | null;
}

export interface CallScoringCallDetailPrepEntry {
  entry: string | CallScoringCallDetailPrepEntryChallengeResponse;
  score: number;
}

export interface CallScoringCallDetailPrepDimension {
  name: string;
  weight: number;
  score: number;
  contribution: number;
  entries: CallScoringCallDetailPrepEntry[] | null;
  note: string | null;
  improvement: string | null;
}

export interface CallScoringCallDetailDeliveryNegativeSignal {
  penalty: number;
  reason: string;
}

export interface CallScoringCallDetailDeliveryDimension {
  name: string;
  weight: number;
  score: number;
  contribution: number;
  evidence: string;
  negative_signal: CallScoringCallDetailDeliveryNegativeSignal | null;
}

export interface CallScoringCallDetailCoachingItem {
  source: "prep" | "delivery" | string;
  area: string;
  action: string;
  tip: string;
  score: number;
}

export interface CallScoringCallDetailSuccessResponse {
  status: string;
  header: {
    prep_score: number;
    prep_label: string;
    delivery_score: number;
    delivery_label: string;
  };
  outcome: {
    label: string;
    description: string;
    score: number;
  };
  preparation: {
    available: boolean;
    dimensions: CallScoringCallDetailPrepDimension[];
    final_score: number;
    message: string | null;
  };
  delivery: {
    dimensions: CallScoringCallDetailDeliveryDimension[];
    final_score: number;
  };
  coaching: CallScoringCallDetailCoachingItem[];
}

export interface CallScoringCallDetailTranscriptInsufficientResponse {
  status: "TRANSCRIPT_INSUFFICIENT";
  message?: string;
}

export type CallScoringCallDetailResponse =
  | CallScoringCallDetailSuccessResponse
  | CallScoringCallDetailTranscriptInsufficientResponse;

export interface CallScoringDraftEmailSection {
  description: string;
  is_liked: boolean;
  is_disliked: boolean;
}

export interface CallScoringDraftEmailSuccessResponse {
  status?: string;
  subject: CallScoringDraftEmailSection;
  message: CallScoringDraftEmailSection;
}

export interface CallScoringDraftEmailNoEmailResponse {
  status: "NO_EMAIL_FOUND";
  message?: string;
}

export type CallScoringDraftEmailResponse =
  | CallScoringDraftEmailSuccessResponse
  | CallScoringDraftEmailNoEmailResponse;

export type CallScoringDraftEmailSectionKey = "subject" | "message";

export interface CallScoringDraftEmailFeedbackRequest {
  section: CallScoringDraftEmailSectionKey;
  is_liked: boolean;
  is_disliked: boolean;
}

export interface CallScoringDraftEmailFeedbackResponse {
  section: CallScoringDraftEmailSectionKey;
  is_liked: boolean;
  is_disliked: boolean;
}

function appendCallScoringEmail(
  params: URLSearchParams,
  email?: string | null,
) {
  const trimmed = email?.trim();
  if (trimmed) {
    params.set("email", trimmed);
  }
}

export class CallScoringService {
  static async getStats({
    email,
    startDate,
    endDate,
  }: GetCallScoringStatsParams): Promise<CallScoringStatsResponse> {
    const search = new URLSearchParams();
    appendCallScoringEmail(search, email);
    appendDateRange(search, startDate, endDate);

    return clientApi
      .get(`call-scoring/stats?${search.toString()}`)
      .json<CallScoringStatsResponse>();
  }

  static async getAccounts({
    email,
    startDate,
    endDate,
    search,
    page = 1,
    pageSize = 10,
  }: GetCallScoringAccountsParams): Promise<CallScoringAccountsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    appendCallScoringEmail(params, email);
    appendDateRange(params, startDate, endDate);
    if (search) params.set("search", search);

    return clientApi
      .get(`call-scoring/accounts?${params.toString()}`)
      .json<CallScoringAccountsResponse>();
  }

  static async getAccountHeader({
    accountId,
    email,
    startDate,
    endDate,
  }: GetCallScoringAccountHeaderParams): Promise<CallScoringAccountHeaderResponse> {
    const params = new URLSearchParams();
    appendCallScoringEmail(params, email);
    appendDateRange(params, startDate, endDate);

    return clientApi
      .get(`call-scoring/accounts/${accountId}/header?${params.toString()}`)
      .json<CallScoringAccountHeaderResponse>();
  }

  static async getAccountCalls({
    accountId,
    email,
    startDate,
    endDate,
    page = 1,
    pageSize = 10,
  }: GetCallScoringAccountCallsParams): Promise<CallScoringAccountCallsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    appendCallScoringEmail(params, email);
    appendDateRange(params, startDate, endDate);

    return clientApi
      .get(`call-scoring/accounts/${accountId}/calls?${params.toString()}`)
      .json<CallScoringAccountCallsResponse>();
  }

  static async getCallDetail(meetingId: number): Promise<CallScoringCallDetailResponse> {
    return clientApi
      .get(`call-scoring/call/${meetingId}`)
      .json<CallScoringCallDetailResponse>();
  }

  static async getDraftEmail(meetingId: number): Promise<CallScoringDraftEmailResponse> {
    return clientApi
      .get(`call-scoring/call/${meetingId}/draft-email`)
      .json<CallScoringDraftEmailResponse>();
  }

  static async submitDraftEmailFeedback(
    meetingId: number,
    payload: CallScoringDraftEmailFeedbackRequest,
  ): Promise<CallScoringDraftEmailFeedbackResponse> {
    return clientApi
      .post(
        `call-scoring/calls/${meetingId}/draft-email-feedback`,
        {
          json: payload,
        },
      )
      .json<CallScoringDraftEmailFeedbackResponse>();
  }
}

